import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { PenLine, Heart } from "lucide-react";
import { listMessages, createMessage, getReactions, addReaction, formatApiError } from "@/lib/api";

const EMOJIS = ["❤️", "🎂", "🎉", "🌹", "🙏", "👑", "✨", "🥳"];
const STORAGE_KEY = "agnes_reacted";

function getReacted() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
}
function markReacted(messageId, emoji) {
  const reacted = getReacted();
  reacted[`${messageId}::${emoji}`] = true;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(reacted)); } catch {}
}
function hasReacted(messageId, emoji) {
  return !!getReacted()[`${messageId}::${emoji}`];
}

function ReactionBar({ messageId }) {
  const [reactions, setReactions] = useState({});
  const [myReactions, setMyReactions] = useState({});
  const [pending, setPending] = useState(null);

  useEffect(() => {
    getReactions(messageId)
      .then((data) => {
        const map = {};
        data.forEach((r) => { map[r.emoji] = r.count; });
        setReactions(map);
      })
      .catch(() => {});
    const mine = {};
    EMOJIS.forEach((e) => { if (hasReacted(messageId, e)) mine[e] = true; });
    setMyReactions(mine);
  }, [messageId]);

  const react = async (emoji) => {
    if (pending || myReactions[emoji]) return;
    setPending(emoji);
    try {
      const updated = await addReaction(messageId, emoji);
      setReactions((prev) => ({ ...prev, [updated.emoji]: updated.count }));
      markReacted(messageId, emoji);
      setMyReactions((prev) => ({ ...prev, [emoji]: true }));
    } catch {
      toast.error("Couldn't save reaction — please try again.");
    } finally {
      setPending(null);
    }
  };

  const activeReactions = EMOJIS.filter(e => reactions[e] || myReactions[e]);
  if (activeReactions.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {activeReactions.map((e) => {
        const already = myReactions[e];
        return (
          <button
            key={e}
            type="button"
            onClick={() => react(e)}
            disabled={already || pending === e}
            className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-sm transition-all
              ${already ? "bg-terracotta/20 text-terracotta font-semibold cursor-default" : "bg-forest/5 text-forest/60 hover:bg-terracotta/10 hover:scale-110"}`}
          >
            {e}{reactions[e] ? <span className="text-[10px] ml-0.5">{reactions[e]}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function Signature({ msg, index, onAdmin, onDelete }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.35) }}
      data-testid="fan-wall-message-card"
      className="relative py-4 border-b border-dashed border-[#c8a96a]/40 last:border-0"
    >
      <div className="flex items-start gap-3">
        <span className="mt-1 text-xl shrink-0">{msg.emoji || "✍️"}</span>
        <div className="flex-1 min-w-0">
          <p className="font-hand text-xl leading-snug text-forest/90">{msg.message}</p>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span className="font-hand text-base text-terracotta">— {msg.name}</span>
            <span className="text-[10px] uppercase tracking-widest text-forest/35">
              {new Date(msg.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            </span>
          </div>
          <ReactionBar messageId={msg.id} />
        </div>
        {onAdmin && (
          <button
            onClick={() => onDelete(msg.id)}
            data-testid={`admin-delete-message-${msg.id}`}
            className="shrink-0 rounded-full bg-destructive/90 px-2 py-0.5 text-[10px] font-medium text-white opacity-70 hover:opacity-100"
          >
            Hide
          </button>
        )}
      </div>
    </motion.div>
  );
}

export default function FanWall({ admin = false, onAdminDelete }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", message: "", emoji: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    listMessages().then(setMessages).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please add your name and a message.");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createMessage({ name: form.name, message: form.message, emoji: form.emoji || null });
      setMessages((prev) => [created, ...prev]);
      setForm({ name: "", message: "", emoji: "" });
      toast.success("Your signature is on the card! 🎉");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = onAdminDelete
    ? async (id) => { await onAdminDelete(id); setMessages((prev) => prev.filter((m) => m.id !== id)); }
    : undefined;

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_400px] lg:items-start">

      {/* Card interior — signatures */}
      <div
        className="relative rounded-3xl shadow-2xl overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #fffdf6 0%, #fdf6e3 60%, #fef9ee 100%)",
          border: "1.5px solid #d4a847",
          boxShadow: "0 4px 40px 0 rgba(180,140,60,0.13), 0 1.5px 0 0 #c8a96a inset",
        }}
      >
        {/* Card top decoration */}
        <div className="px-8 pt-8 pb-4 border-b border-dashed border-[#c8a96a]/30"
          style={{ background: "linear-gradient(90deg, #fdf3d0 0%, #fef9ee 100%)" }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-serif text-xs uppercase tracking-[0.25em] text-[#b8943f]">Happy 107th Birthday</p>
              <p className="font-hand text-3xl text-forest mt-0.5">Agnes Murphy 🎂</p>
            </div>
            <div className="text-right">
              <span className="inline-block rounded-full bg-terracotta/10 px-3 py-1 text-sm font-medium text-terracotta" data-testid="fan-wall-count">
                {messages.length} {messages.length === 1 ? "signature" : "signatures"}
              </span>
            </div>
          </div>
        </div>

        {/* Signatures */}
        <div className="px-8 py-6 min-h-[320px]">
          {loading ? (
            <div className="space-y-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-lg bg-[#c8a96a]/10" />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="text-5xl mb-4">✍️</span>
              <p className="font-hand text-3xl text-forest/60">Be the first to sign!</p>
              <p className="mt-2 text-sm text-forest/40">Your message will appear right here on the card.</p>
            </div>
          ) : (
            <AnimatePresence>
              {messages.map((m, i) => (
                <Signature key={m.id} msg={m} index={i} onAdmin={admin} onDelete={handleDelete} />
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Card bottom flourish */}
        <div className="px-8 py-4 border-t border-dashed border-[#c8a96a]/30 text-center"
          style={{ background: "linear-gradient(90deg, #fef9ee 0%, #fdf3d0 100%)" }}
        >
          <p className="font-hand text-base text-[#b8943f] opacity-70">
            August 22, 2026 · 107 Years of Grace & Joy 🌹
          </p>
        </div>
      </div>

      {/* Signing form */}
      <div className="lg:sticky lg:top-24">
        <form
          onSubmit={submit}
          data-testid="fan-wall-submit-form"
          className="relative rounded-3xl overflow-hidden shadow-xl"
          style={{
            background: "linear-gradient(160deg, #fffdf6 0%, #fdf6e3 100%)",
            border: "1.5px solid #d4a847",
          }}
        >
          {/* Form header */}
          <div className="px-7 pt-7 pb-5 border-b border-dashed border-[#c8a96a]/30"
            style={{ background: "linear-gradient(90deg, #fdf3d0 0%, #fef9ee 100%)" }}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-terracotta/10">
                <PenLine className="h-5 w-5 text-terracotta" />
              </div>
              <div>
                <p className="font-hand text-2xl text-forest leading-none">Sign the card</p>
                <p className="text-xs text-forest/50 mt-0.5">Your name goes down in history ✨</p>
              </div>
            </div>
          </div>

          <div className="px-7 py-6 space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[#b8943f] mb-1.5">Your name</label>
              <input
                data-testid="fan-wall-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane from Dublin"
                maxLength={100}
                className="w-full rounded-xl border border-[#d4a847]/40 bg-white/70 px-4 py-3 font-hand text-lg text-forest outline-none transition focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 placeholder:text-forest/30 placeholder:font-sans placeholder:text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[#b8943f] mb-1.5">Your birthday message</label>
              <textarea
                data-testid="fan-wall-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Wishing you joy, love, and many more years…"
                maxLength={1000}
                rows={4}
                className="w-full resize-y rounded-xl border border-[#d4a847]/40 bg-white/70 px-4 py-3 font-hand text-lg text-forest outline-none transition focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 placeholder:text-forest/30 placeholder:font-sans placeholder:text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-[#b8943f] mb-2">Add a sticker</label>
              <div className="flex flex-wrap gap-2">
                {EMOJIS.map((e) => (
                  <button
                    type="button"
                    key={e}
                    onClick={() => setForm({ ...form, emoji: form.emoji === e ? "" : e })}
                    data-testid={`emoji-${e}`}
                    className={`h-10 w-10 rounded-full border text-xl transition-all ${form.emoji === e ? "border-terracotta bg-terracotta/15 scale-110 shadow-sm" : "border-[#d4a847]/30 bg-white/50 hover:border-terracotta/50 hover:scale-105"}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="fan-wall-submit-button"
              className="w-full flex items-center justify-center gap-2 rounded-full py-3.5 font-semibold text-cream transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              style={{ background: "linear-gradient(90deg, #c0392b 0%, #e67e22 100%)" }}
            >
              <Heart className="h-4 w-4" />
              {submitting ? "Signing…" : "Sign the Birthday Card"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
